import { defineStore } from 'pinia'
import { useAlertsStore } from '@/store/pinia/alerts'
import { useAuthStore } from '@/store/pinia/auth'
import { useOrganizationStore } from '@/store/pinia/organization'
import { plans } from '@/utils/config/billing'
import { decodeJWT } from '@/utils/helpers'
import { makeResetStoreAction, makeSetErrorsAction, makeClearErrorsAction } from '@/utils/helpers/pinia'
import i18n from '@/utils/plugins/i18n'

export const state = () => ({
  blocked: [],
  subscription: {},
  errors: {
    billing: [],
  },
})

export const useBillingStore = defineStore('billing', {
  state,
  getters: {
    hasActiveBillingPlan: (state) => {
      const blocked = _.get(state, 'blocked', [])
      const blockedStatuses = ['require_payment', 'require_plan', 'expired_free_trial', 'expired_subscription']
      return _.isEmpty(_.intersection(blocked, blockedStatuses))
    },
    subscriptionPlan: (state) => {
      return _.$get(state.subscription, 'plan', {})
    },
    requirePayment: (state) => {
      const blocked = _.get(state, 'blocked', [])
      const paymentStatuses = ['require_payment', 'expired_free_trial']
      return !_.isEmpty(_.intersection(blocked, paymentStatuses))
    },
    requirePlan: (state) => {
      return (state.blocked || []).includes('require_plan')
    },
    daysRemaining: (state) => {
      const { expires_at: expireTime } = state.subscription
      /**
     * We have a small issue with time check:
     * when starting the free trial, sometimes it displays 31 days left,
     * then after refresh or passing some time it displays back 30 days left.
     **/
      return $date.differenceInDays($date.fromUnixTime(expireTime), new Date())
    },
    hoursRemaining: (state) => {
      const { expires_at: expireTime } = state.subscription
      return $date.differenceInHours($date.fromUnixTime(expireTime), new Date())
    },
    trialExpired: (state) => {
      const { blocked, isFreeTrial, subscription: { expires_at: expireTime } } = state
      return isFreeTrial && _.some([
        $date.differenceInMilliseconds($date.fromUnixTime(expireTime), new Date()) <= 0,
        blocked.includes('expired_free_trial'),
      ])
    },
    isFreeTrial: (state) => {
      const planName = state.subscriptionPlan?.name || ''
      return planName.toLowerCase() === plans.freeTrial.name.toLowerCase()
    },
    isEssential: (state) => {
      const planName = state.subscriptionPlan?.name || ''
      return planName.toLowerCase() === plans.essential.name.toLowerCase()
    },
    isEnterprise: (state) => {
      const planName = state.subscriptionPlan?.name || ''
      return planName.toLowerCase() === plans.enterprise.name.toLowerCase()
    },
    planName: (state) => {
      const {
        isFreeTrial, isEssential, isEnterprise, trialExpired,
        subscriptionPlan: { name: planName } = {},
      } = state
      const showEnterPrise = !_.some([isFreeTrial, isEssential, isEnterprise, trialExpired])
      if (planName) return planName
      return showEnterPrise ? 'Enterprise' : plans.freeTrial.name
    },
  },
  actions: {
    /**
     * Clear the current errors list.
     */
    CLEAR_BILLING_ERRORS: makeClearErrorsAction(state),

    /**
     * Reset the whole module state to its initial shape.
     */
    RESET_BILLING_STATE: makeResetStoreAction(state),

    /**
     * Replace the current errors array.
     */
    SET_ERRORS: makeSetErrorsAction(),

    /**
     * Set blocked statuses in state.
     * @param {Array} blocked
     * @returns {void}
     */
    SET_BLOCKED (blocked = []) {
      this.blocked = blocked
    },

    /**
     * Set subscription details in state.
     * @param {Object} subscription
     * @returns {void}
     */
    SET_SUBSCRIPTION (subscription = {}) {
      this.subscription = subscription
    },

    /**
     * Get organization billing status from JWT.
     * @returns {void}
     */
    SET_ORG_BILLING_STATUS () {
      const authStore = useAuthStore()
      const { blocked, subscription } = decodeJWT(authStore.jwt?.split('.')[1])?.cycloid?.organization || {}
      this.SET_BLOCKED (blocked)
      this.SET_SUBSCRIPTION (subscription)
    },

    /**
     * Create subscription.
     * @param {Object} params
     * @param {Object} params.subscriptionData
     * @returns {Promise<void>}
     */
    async CREATE_SUBSCRIPTION ({ subscriptionData }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_BILLING_ERRORS('billing')

      try {
        const { data, errors } = (await this.$cycloid.ydAPI.createSubscription(
          orgStore.orgCanonical,
          subscriptionData,
        )) || {}

        if (data) {
          // Refresh auth token and billing status
          this.SET_ORG_BILLING_STATUS()
          alertStore.SHOW_ALERT({
            type: 'success',
            content: i18n.global.t('alerts.success.organization.subscription.created', { planName: subscriptionData.name }),
          })
        }

        if (errors) {
          this.SET_ERRORS({ key: 'billing', errors })
        }
      } catch (error) {
        this.SET_ERRORS({ key: 'billing', errors: [{ message: error.message }] })
      }
    },
  },
})
